import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

/**
 * The behavioural moments the parcours funnel (taux 3/4/5) is built on. Kept in
 * step with the API's accepted types; a join is deliberately absent, it is read
 * from events.attendees server-side, never emitted.
 */
export type JourneyEventType = 'profile_completed' | 'search_performed' | 'activity_viewed';

/**
 * Emits one journey event to our own API (not Firebase, which AnalyticsService
 * owns for product metrics). Fire-and-forget: a dropped event must never affect
 * the user flow, for the same reason AnalyticsService states beside its own
 * calls.
 *
 * Called directly with HttpClient rather than the generated SDK: the route is
 * new, and regenerating the client from a running API rewrites hundreds of
 * files (the reason take-over-activity.service.ts gives). The member is taken
 * from the token by the interceptor; no identity is sent in the body.
 */
@Injectable({ providedIn: 'root' })
export class JourneyEventService {
  private readonly http = inject(HttpClient);

  /**
   * One emission per key per app session. The funnel reads the first occurrence
   * of each type per member, so re-sending within a session adds rows and
   * nothing else; bounding it here keeps a browsing session from writing a row
   * for every activity opened. A new session (app relaunch) emits again, which
   * is wanted: it keeps recency without inflating the first-occurrence the
   * funnel uses.
   */
  private readonly emittedThisSession = new Set<string>();

  emit(type: JourneyEventType, metadata?: Record<string, unknown>, sessionKey: string = type): void {
    if (this.emittedThisSession.has(sessionKey)) {
      return;
    }
    // Marked before the request so a burst of identical triggers sends once.
    this.emittedThisSession.add(sessionKey);
    this.http.post(`${environment.api}/api/v1/events/journey`, { type, ...(metadata ? { metadata } : {}) }).subscribe({
      error: () => {
        // Swallowed on purpose. Free the key so a later trigger this session can
        // try again, rather than losing the moment to one dropped response.
        this.emittedThisSession.delete(sessionKey);
      },
    });
  }
}
